import { Scale, X } from 'lucide-react';
import { useState } from 'react';
import { usePortalAuth } from './PortalAuthContext';
import { usePortalDestination } from './PortalDestinationContext';

const DISMISS_KEY = 'dutilyne_growth_nudge_dismissed';
const DAY = 86400000;

/** Due when a child's weight is missing or older than their interval
 *  (monthly under 2, every 3 months after that). */
export function growthNudgeDue(family) {
  const now = Date.now();
  return family.filter((m) => {
    if (!m.dob) return false;
    const age = (now - new Date(m.dob).getTime()) / (365.25 * DAY);
    if (age <= 0 || age >= 12) return false;
    if (!m.weight_kg || !m.weight_recorded_at) return true;
    const days = (now - new Date(m.weight_recorded_at).getTime()) / DAY;
    return days > (age < 2 ? 30 : 90);
  });
}

export const PortalGrowthNudge = () => {
  const { family } = usePortalAuth();
  const { href } = usePortalDestination();
  const [dismissed, setDismissed] = useState(null);
  const due = growthNudgeDue(family);
  const key = due.map((m) => m.patient_user_id).sort().join(',');
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
  })();
  // A newly due child brings the banner back, even inside the 7-day dismissal.
  const hidden = dismissed === key || (stored.key === key && stored.at > Date.now() - 7 * DAY);
  if (hidden || due.length === 0) return null;
  const names = due.map((m) => m.name.split(' ')[0]);
  const who = names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0];
  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2.5 flex flex-wrap items-center justify-center gap-2"
      data-testid="portal-growth-nudge">
      <Scale className="w-4 h-4 shrink-0" />
      <span>Update the weight for {who} so medicine doses stay accurate as they grow.</span>
      <a href={href('family')} className="underline font-medium" data-testid="portal-growth-nudge-link">Update now</a>
      <button type="button" aria-label="Dismiss" className="opacity-60 hover:opacity-100"
        onClick={() => { localStorage.setItem(DISMISS_KEY, JSON.stringify({ key, at: Date.now() })); setDismissed(key); }}
        data-testid="portal-growth-nudge-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
